import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { MembershipResponse } from '@/lib/definitions/api.types'
import { FLAGS, useFeatureFlag } from '@/lib/hooks/useFeatureFlag'
import { REFRESH_USER, useAuth } from '@/lib/provider/AuthProvider'
import { useTenant } from '@/lib/provider/TenantProvider'
import { useLoadMemberships } from '@/lib/queries/Membership.queries'

interface CompanySelection {
  memberships: MembershipResponse[]
  activeCustomerId?: number
  canSwitchCompany: boolean
  needsChoice: boolean
  selectCompany: (
    membership: MembershipResponse,
    options?: { goHome?: boolean }
  ) => void
}

export const useCanCreateAnotherCompany = (): boolean => {
  const [auth] = useAuth()
  const areMembershipsEnabled = useFeatureFlag(FLAGS.COMPANY_MULTI_ACCESS)
  const isSelfServiceCreationEnabled = useFeatureFlag(
    FLAGS.COMPANY_SELF_SERVICE_CREATION
  )

  return (
    Boolean(auth.user?.customer_id) &&
    areMembershipsEnabled &&
    isSelfServiceCreationEnabled
  )
}

export const useCompanySelection = (): CompanySelection => {
  const [auth, authDispatch] = useAuth()
  const {
    customerId,
    hasChosenCompany,
    setCustomerIdHandler,
    removeCustomerIdHandler,
  } = useTenant()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const areMembershipsEnabled = useFeatureFlag(FLAGS.COMPANY_MULTI_ACCESS)
  const hasCompany = Boolean(auth.user?.customer_id)
  const isMembershipQueryEnabled = areMembershipsEnabled && hasCompany
  const { data } = useLoadMemberships(isMembershipQueryEnabled)
  const memberships = data ?? []

  const activeCustomerId = customerId
    ? Number(customerId)
    : memberships.find((membership) => membership.is_direct)?.customer_id

  const selectCompany = useCallback(
    (membership: MembershipResponse, options?: { goHome?: boolean }) => {
      const isAlreadyActive = membership.customer_id === activeCustomerId

      if (membership.is_direct) {
        removeCustomerIdHandler({ markChosen: true })
      } else {
        setCustomerIdHandler(membership.customer_id.toString(), 'membership')
      }

      if (isAlreadyActive) return

      queryClient.clear()
      authDispatch({ type: REFRESH_USER })
      if (options?.goHome !== false) navigate('/')
    },
    [
      activeCustomerId,
      authDispatch,
      navigate,
      queryClient,
      removeCustomerIdHandler,
      setCustomerIdHandler,
    ]
  )

  return {
    memberships,
    activeCustomerId,
    canSwitchCompany: memberships.length > 1,
    needsChoice: memberships.length > 1 && !hasChosenCompany,
    selectCompany,
  }
}
